import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { isDsd, getDsdSampleRateHz } from '../utils/formatDetection';
import { resolveExclusiveDeviceName, logLocalOutputExclusive, logLocalOutputShared } from '../utils/localOutputLogging';
import { clearExclusiveErrorOnPlaybackConfirmed, exclusivePlayWithDeviceBusyRetry, isRecoverableExclusiveFailure, releaseSharedFallbackBeforeExclusive, shouldShowFatalExclusiveError } from '../utils/exclusiveFallbackUI';
import { setExclusiveOpenInFlight } from './exclusiveOpenInFlight';
import { applyExclusivePlayResult, noteExclusiveDispatchStart } from '../utils/sharedModeNotice';
import { beginExclusiveDispatch, handleExclusivePlayResolvedAfterAbandon, isExclusiveDispatchAbandoned, isLatestExclusiveDispatch, noteExclusiveOpenTimeout } from './exclusiveTimeoutFallback';

type ConnectorModule = typeof import('../lib/connector');

export async function dispatchExclusivePlayUri(options: {
  uri: string;
  track: any;
  suppressExclusiveErrorUi?: boolean;
  getConnector: () => Promise<ConnectorModule>;
  startExclusivePoller: () => void;
  onSuccess: () => void;
}): Promise<boolean> {
  const dacName = resolveExclusiveDeviceName(
    useMaestroStore.getState().discovery?.activeZone as any,
    useMaestroStore.getState().selectedUsbDac,
  );
  if (!dacName) return false;
  console.log('[TCmd] playUri → EXCLUSIVE BIT-PERFECT path for USB DAC:', dacName);
  diagnosticLog('info', 'transport', 'exclusive_play_start', { dac: dacName, rendererType: 'local-native' });
  const gen = beginExclusiveDispatch(); noteExclusiveDispatchStart(); // #10426-a1 guard token — taken BEFORE the pre-stops
  const C = await options.getConnector();
  await releaseSharedFallbackBeforeExclusive();
  await C.exclusiveStop().catch(() => {});
  if (isExclusiveDispatchAbandoned(gen)) return true; // superseded during the pre-stops — never send exclusive_play
  const trackIsDsd = isDsd(options.track);
  const srRaw = options.track?.sampleRate || options.track?.sample_rate || 44100;
  const sampleRate = trackIsDsd ? (getDsdSampleRateHz(options.track) ?? 2822400) : (srRaw < 1000 ? srRaw * 1000 : srRaw);
  const bd = trackIsDsd ? 1 : (options.track?.bitDepth || options.track?.bit_depth || 16);
  setExclusiveOpenInFlight(true);
  try {
    const result = await exclusivePlayWithDeviceBusyRetry(() => C.exclusivePlay(dacName, options.uri, sampleRate, bd, trackIsDsd));
    if (await handleExclusivePlayResolvedAfterAbandon(gen)) return true; // shared fallback already owns output
    if (applyExclusivePlayResult(result, dacName) === 'shared') logLocalOutputShared(dacName, 'connector_output_mode_shared'); else logLocalOutputExclusive(dacName, sampleRate, bd);
  } catch (err: any) {
    const msg = err?.message || String(err); if (isExclusiveDispatchAbandoned(gen)) throw err; await noteExclusiveOpenTimeout(gen, msg, options.track);
    console.error('[TCmd] Exclusive play failed:', msg);
    logLocalOutputShared(dacName, `exclusive_open_failed:${msg}`);
    const recoverable = isRecoverableExclusiveFailure(msg);
    diagnosticLog((options.suppressExclusiveErrorUi || recoverable) ? 'info' : 'error', 'transport', 'exclusive_play_failed', {
      dac: dacName, error: msg, recoverable, suppressed: !!options.suppressExclusiveErrorUi,
    });
    if (!options.suppressExclusiveErrorUi && shouldShowFatalExclusiveError(msg)) {
      useMaestroStore.getState().setPlaybackState('paused');
      useMaestroStore.getState().setExclusiveError?.(msg);
    }
    throw err;
  } finally {
    if (isLatestExclusiveDispatch(gen)) setExclusiveOpenInFlight(false);
  }
  clearExclusiveErrorOnPlaybackConfirmed();
  options.startExclusivePoller();
  options.onSuccess();
  console.log('[TCmd] playUri EXCLUSIVE SUCCESS');
  return true;
}
